import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, unref as _unref, isRef as _isRef, vModelText as _vModelText, withDirectives as _withDirectives, Fragment as _Fragment, createVNode as _createVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "line-gutter" }
const _hoisted_2 = { class: "line-number" }
const _hoisted_3 = {
  key: 0,
  class: "line-grip",
  title: "拖动调整组内顺序",
  "aria-label": "拖动台词排序"
}
const _hoisted_4 = { class: "line-body" }
const _hoisted_5 = {
  class: "line-actions",
  role: "group",
  "aria-label": "台词操作"
}
const _hoisted_6 = ["disabled", "title"]
const _hoisted_7 = ["title"]
const _hoisted_8 = ["title"]
const _hoisted_9 = {
  key: 2,
  class: "line-clip-summary"
}
const _hoisted_10 = ["aria-label"]

import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch, type Ref } from "vue";
import type { TextPreviewLine } from "../utils/dialogueTextPreview";
import type { DialogueTextEdit, DialogueTextField } from "../utils/dialogueTextEditing";
import { normalizeDialogueInput, sanitizeDialogueInput, preventDialogueLineBreak } from "../utils/dialogueTextInput";
import { dialogueStyleShowsTitle, getDialogueStyles } from "../config/dialogueStyleRegistry";
import DialogueStyleSelect from "./DialogueStyleSelect.vue";

export default /*@__PURE__*/_defineComponent({
  __name: 'DialogueTextLine',
  props: {
    line: {},
    speakerAlias: {},
    index: {},
    canMoveUp: { type: Boolean },
    canMoveDown: { type: Boolean },
    movable: { type: Boolean },
    canInsert: { type: Boolean, default: true },
    repeatSpeaker: { type: Boolean }
  },
  emits: ["edit", "move", "insert", "configure", "addDialogue", "remove", "pickSpeaker"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const avatarLabel = computed(() => props.speakerAlias || props.line.speaker.trim().slice(0, 1) || "旁");
const emit = __emit;
const field = (name: DialogueTextField) => computed({
  get: () => props.line[name],
  set: (value: string) => emit("edit", { nodeId: props.line.nodeId, clipId: props.line.clipId, field: name, value: name === "content" ? normalizeDialogueInput(value) : value }),
});
const speaker = field("speaker");
const subtitle = field("subtitle");
const content = field("content");
const style = field("style");
const dialogueStyles = inject<Ref<ReturnType<typeof getDialogueStyles>>>("dialogueStyleOptions", computed(() => getDialogueStyles()));
const showTitle = computed(() => dialogueStyleShowsTitle(style.value, dialogueStyles.value));
const textarea = ref<HTMLTextAreaElement>();
function resize() {
  if (!textarea.value) return;
  textarea.value.style.height = "0px";
  textarea.value.style.height = `${textarea.value.scrollHeight + textarea.value.offsetHeight - textarea.value.clientHeight}px`;
}
let observer: ResizeObserver | undefined;
let measuredWidth = 0;
onMounted(() => {
  resize();
  observer = new ResizeObserver(() => {
    const width = textarea.value?.clientWidth ?? 0;
    if (width !== measuredWidth) { measuredWidth = width; resize(); }
  });
  if (textarea.value) observer.observe(textarea.value);
});
onBeforeUnmount(() => observer?.disconnect());
watch(() => props.line.content, () => nextTick(resize));
const hue = computed(() => [...props.line.speaker].reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) % 360, 210));
function keydown(event: KeyboardEvent) {
  if (event.isComposing || event.keyCode === 229) return;
  if (event.key === "Enter" && (event.target as HTMLElement)?.tagName === "TEXTAREA") event.preventDefault();
  if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) { event.preventDefault(); if (props.canInsert) emit("insert"); }
  if (event.altKey && ["ArrowUp", "ArrowDown"].includes(event.key)) {
    event.preventDefault();
    if (event.key === "ArrowUp" ? props.canMoveUp : props.canMoveDown) emit("move", event.key === "ArrowUp" ? -1 : 1);
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["script-line", { 'repeat-speaker': __props.repeatSpeaker }]),
    style: _normalizeStyle({ '--speaker-hue': hue.value }),
    onKeydown: keydown
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("span", _hoisted_2, _toDisplayString(String(__props.index + 1).padStart(2, '0')), 1),
      (__props.movable)
        ? (_openBlock(), _createElementBlock("span", _hoisted_3, "⠿"))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("button", {
          type: "button",
          disabled: !__props.canInsert,
          title: __props.canInsert ? '插入台词（Ctrl+Enter）' : '此节点含多段对话，请在 Timeline 中添加',
          "aria-label": "插入台词",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('insert')))
        }, "＋", 8, _hoisted_6),
        _createElementVNode("button", {
          type: "button",
          title: "在节点图配置此句 Clip",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('configure')))
        }, "Clip ↗"),
        _createElementVNode("button", {
          type: "button",
          class: "delete-line",
          "aria-label": "删除对话",
          title: (__props.line.dialogueCount ?? 1) > 1 ? '删除当前 Dialogue Clip，保留其他台词与演出，可立即撤销' : __props.line.clipCount ? `删除此句及附带的 ${__props.line.clipCount} 个 Clip（带选项卡时保留选项与其他 Clip）` : '删除此句对话，可立即撤销',
          onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('remove')))
        }, "删除", 8, _hoisted_7)
      ]),
      (__props.line.hasDialogue)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("div", {
              class: _normalizeClass(["line-identity", { 'without-title': !showTitle.value }])
            }, [
              (showTitle.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createElementVNode("button", {
                      type: "button",
                      class: _normalizeClass(["speaker-avatar", { 'has-alias': __props.speakerAlias }]),
                      title: `${avatarLabel.value} · 从预设实体选择说话人`,
                      "aria-label": "从预设实体选择说话人",
                      "aria-haspopup": "dialog",
                      onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (emit('pickSpeaker')), ["stop"]))
                    }, _toDisplayString(avatarLabel.value), 11, _hoisted_8),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (_isRef(speaker) ? (speaker).value = $event : null)),
                      class: "speaker-input",
                      "aria-label": "说话人",
                      placeholder: "旁白 / 说话人"
                    }, null, 512), [
                      [_vModelText, _unref(speaker)]
                    ]),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_isRef(subtitle) ? (subtitle).value = $event : null)),
                      class: "subtitle-input",
                      "aria-label": "副标题 Subtitle",
                      title: "副标题（Subtitle）",
                      placeholder: "副标题（可选）"
                    }, null, 512), [
                      [_vModelText, _unref(subtitle)]
                    ])
                  ], 64))
                : _createCommentVNode("", true),
              _createVNode(DialogueStyleSelect, {
                modelValue: _unref(style),
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (_isRef(style) ? (style).value = $event : null)),
                options: _unref(dialogueStyles)
              }, null, 8, ["modelValue", "options"])
            ], 2),
            _withDirectives(_createElementVNode("textarea", {
              ref_key: "textarea",
              ref: textarea,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => (_isRef(content) ? (content).value = $event : null)),
              rows: "1",
              "aria-label": "台词",
              placeholder: "输入对话，换行请写 \\n",
              onBeforeinput: _cache[8] || (_cache[8] = 
//@ts-ignore
(...args) => (_unref(preventDialogueLineBreak) && _unref(preventDialogueLineBreak)(...args))),
              onInput: _cache[9] || (_cache[9] = ($event: any) => {_unref(sanitizeDialogueInput)($event); resize()})
            }, null, 544), [
              [_vModelText, _unref(content)]
            ])
          ], 64))
        : (_openBlock(), _createElementBlock("button", {
            key: 1,
            class: "add-dialogue",
            type: "button",
            onClick: _cache[10] || (_cache[10] = ($event: any) => (emit('addDialogue')))
          }, "＋ 为此段添加台词")),
      (__props.line.clipCount > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createElementVNode("button", {
              type: "button",
              class: "clip-count",
              "aria-label": `此节点含 ${__props.line.clipCount} 个演出 Clip，点击配置`,
              title: "在节点图查看此节点的 Clip",
              onClick: _cache[11] || (_cache[11] = ($event: any) => (emit('configure')))
            }, _toDisplayString(__props.line.clipCount) + " 个 Clip ↗", 9, _hoisted_10)
          ]))
        : _createCommentVNode("", true)
    ])
  ], 38))
}
}

})